/**
 * Light or dark, chosen by each person with the sun / moon button in the top bar, next to their avatar (27 Oct 2026;
 * it was in the account menu). Light is the default; the choice is kept in this browser. Charts follow because they
 * watch the data-theme attribute.
 *
 * One choice for the whole page (27 Oct 2026): the top bar's button and the command bar's "Switch light / dark theme"
 * share it, so each always shows what is on screen. Another tab changing it is followed here too.
 */
import { useEffect, useSyncExternalStore } from 'react';

export type Theme = 'light' | 'dark';
const KEY = 'ap-theme';

export function storedTheme(): Theme {
  try {
    return localStorage.getItem(KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

export function applyTheme(t: Theme): void {
  document.documentElement.dataset.theme = t;
  document.querySelector('meta[name="color-scheme"]')?.setAttribute('content', t);
}

let current: Theme | null = null;
const listeners = new Set<() => void>();
const now = (): Theme => (current ??= storedTheme());

function setTheme(t: Theme): void {
  current = t;
  applyTheme(t);
  listeners.forEach((f) => f());
}

/** Another tab of this site changed it. */
const fromOtherTab = (e: StorageEvent) => e.key === KEY && setTheme(storedTheme());

function subscribe(f: () => void): () => void {
  if (!listeners.size) window.addEventListener('storage', fromOtherTab);
  listeners.add(f);
  return () => {
    listeners.delete(f);
    if (!listeners.size) window.removeEventListener('storage', fromOtherTab);
  };
}

export function useThemeChoice(): [Theme, () => void] {
  const t = useSyncExternalStore(subscribe, now, now);
  useEffect(() => applyTheme(t), [t]);
  return [
    t,
    () => {
      const next: Theme = now() === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(KEY, next);
      } catch {
        // Private windows: the switch still works for this visit.
      }
      setTheme(next);
    },
  ];
}
